// IR -> branded HTML string for the live in-chat preview. Structural output
// must match the Python renderer (agent_runtime/documents/renderers/html.py) —
// see shared/document-conformance/expected-blocks.json.

interface Run { text: string; bold?: boolean; italic?: boolean; link?: string }
interface Block { type: string; [k: string]: any }
export interface DocIR {
  doc_version: number; doc_type: "report" | "deck";
  title: string | null; theme: string; blocks: Block[];
}

const ALLOWED = ["http://", "https://", "mailto:"];

function esc(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function safeLink(url: string): string | null {
  const low = url.trim().toLowerCase();
  return ALLOWED.some((s) => low.startsWith(s)) ? url.trim() : null;
}

function renderRuns(runs: Run[] = []): string {
  return runs.map((r) => {
    let t = esc(r.text);
    if (r.bold) t = `<strong>${t}</strong>`;
    if (r.italic) t = `<em>${t}</em>`;
    if (r.link) {
      const href = safeLink(String(r.link));
      if (href) t = `<a href="${esc(href)}" rel="noopener noreferrer">${t}</a>`;
    }
    return t;
  }).join("");
}

function renderBlock(b: Block): string {
  switch (b.type) {
    case "heading": return `<h${b.level ?? 1}>${esc(b.text)}</h${b.level ?? 1}>`;
    case "paragraph": return `<p>${renderRuns(b.runs)}</p>`;
    case "bullets": {
      const tag = b.ordered ? "ol" : "ul";
      const items = (b.items ?? []).map((it: Run[]) => `<li>${renderRuns(it)}</li>`).join("");
      return `<${tag}>${items}</${tag}>`;
    }
    case "table": {
      const head = (b.header ?? []).map((c: string) => `<th>${esc(c)}</th>`).join("");
      const body = (b.rows ?? []).map(
        (row: string[]) => `<tr>${row.map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`
      ).join("");
      return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
    }
    case "image": {
      const src = b.data_url || b.artifact_id || "";
      const cap = b.caption ? `<figcaption>${esc(b.caption)}</figcaption>` : "";
      return `<figure><img src="${esc(src)}" alt="${esc(b.caption ?? "")}">${cap}</figure>`;
    }
    case "page_break": return `<div class="page-break"></div>`;
    case "slide": {
      const bullets = (b.bullets ?? []).map((it: Run[]) => `<li>${renderRuns(it)}</li>`).join("");
      const ul = bullets ? `<ul>${bullets}</ul>` : "";
      return `<section class="slide"><h2>${esc(b.title)}</h2>${ul}</section>`;
    }
    default: return "";
  }
}

// Brand colors mirror shared/brand-tokens.json brand.primary (#48A999).
const THEME_CSS = `
  body{font-family:Inter,system-ui,sans-serif;color:#10302A;background:#F9FAFB;margin:0;line-height:1.55}
  .doc-report{max-width:720px;margin:0 auto;padding:48px 40px}
  h1,h2,h3{color:#48A999;margin:1.2em 0 .4em}
  table{border-collapse:collapse;width:100%;margin:1em 0}
  th,td{border:1px solid #D1EAE5;padding:6px 10px;text-align:left}
  th{background:#D1EAE5;color:#06231F}
  .page-break{break-after:page;border-top:1px dashed #5B6B67;margin:1.5em 0}
  .doc-deck{padding:24px;display:flex;flex-direction:column;gap:20px}
  .slide{border:1px solid #D1EAE5;border-radius:12px;padding:28px 32px;aspect-ratio:16/9;background:#fff}
`;

export function renderDocHtml(ir: DocIR): string {
  const wrapper = ir.doc_type === "deck" ? "doc-deck" : "doc-report";
  const body = (ir.blocks ?? []).map(renderBlock).join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(ir.title)}</title>` +
    `<style>${THEME_CSS}</style></head><body><div class="${wrapper}">${body}</div></body></html>`;
}
